import { Controller } from "@hotwired/stimulus";
import AutoNumeric from "autonumeric";

const SELECTION_EVENTS = ["select", "mouseup", "keyup"];

export default class extends Controller {
  connect() {
    this.usesCalculator = this.data.get("calculator") === "true";
    this.initializeAutoNumeric();
    this.registerFormSubmit();
    if (this.usesCalculator) this.initializeCalculatorMode();
  }

  disconnect() {
    this.destroyAutoNumeric();
    this.unregisterFormSubmit();
    this.teardownCalculatorMode();
  }

  getRawValue() {
    return AutoNumeric.getNumber(this.element);
  }

  initializeAutoNumeric() {
    this.an = new AutoNumeric(this.element, this.autoNumericOptions());
  }

  destroyAutoNumeric() {
    if (this.an) this.an.remove();
  }

  autoNumericOptions() {
    const decimals = Number(this.data.get("decimals"));
    const currency = this.data.get("currency");
    return {
      digitGroupSeparator: ".",
      decimalCharacter: ",",
      currencySymbol: currency ? `${currency} ` : "",
      currencySymbolPlacement: "p",
      decimalPlaces: Number.isFinite(decimals) ? decimals : 2,
    };
  }

  registerFormSubmit() {
    this.form = this.element.closest("form");
    if (!this.form) return;

    this._submitHandler = () => {
      try {
        const raw = this.getRawValue();
        this.element.value = raw != null ? String(raw) : "";
      } catch (_) {
        // ignore conversion errors
      }
    };
    this.form.addEventListener("submit", this._submitHandler);
  }

  unregisterFormSubmit() {
    if (this.form && this._submitHandler)
      this.form.removeEventListener("submit", this._submitHandler);
  }

  initializeCalculatorMode() {
    this._cents = this.initialCents();
    this.ensureInitialDisplay();
    this._hasSelection = false;
    this._selectionHandler = () => this.updateSelectionState();
    SELECTION_EVENTS.forEach((eventName) =>
      this.element.addEventListener(eventName, this._selectionHandler),
    );

    this._keydownHandler = (event) => this.handleCalculatorKeydown(event);
    this.element.addEventListener("keydown", this._keydownHandler);
  }

  teardownCalculatorMode() {
    if (this._keydownHandler)
      this.element.removeEventListener("keydown", this._keydownHandler);
    if (this._selectionHandler)
      SELECTION_EVENTS.forEach((eventName) =>
        this.element.removeEventListener(eventName, this._selectionHandler),
      );
  }

  handleCalculatorKeydown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey) return;

    const key = event.key;
    if (/^[0-9]$/.test(key)) {
      event.preventDefault();
      this.applyDigit(Number(key));
      return;
    }

    if (key === "Backspace") {
      event.preventDefault();
      this.handleBackspace();
      return;
    }

    if (key === "Delete") {
      event.preventDefault();
      this.handleDelete();
    }
  }

  applyDigit(digit) {
    if (this._hasSelection) {
      this._cents = digit;
      this._hasSelection = false;
    } else {
      this._cents = this._cents * 10 + digit;
    }
    this.renderCents();
  }

  handleBackspace() {
    if (this._hasSelection) return this.resetCents();
    this._cents = Math.floor(this._cents / 10);
    this.renderCents();
  }

  handleDelete() {
    if (this._hasSelection) return this.resetCents();
    this._cents = Math.floor(this._cents / 10);
    this.renderCents();
  }

  resetCents() {
    this._cents = 0;
    this._hasSelection = false;
    this.renderCents();
  }

  renderCents() {
    this.writeValue(this._cents / 100);
  }

  writeValue(value) {
    try {
      AutoNumeric.set(this.element, value);
    } catch (_) {
      this.element.value = String(value);
    }
  }

  initialCents() {
    const current = this.getRawValue();
    return Number.isFinite(current) ? Math.round(current * 100) : 0;
  }

  ensureInitialDisplay() {
    if (!this.element.value) this.writeValue(0);
  }

  updateSelectionState() {
    const { selectionStart, selectionEnd } = this.element;
    this._hasSelection =
      selectionStart != null &&
      selectionEnd != null &&
      Number(selectionEnd) > Number(selectionStart);
  }
}
